import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Link2, Unlink, RefreshCw, Zap, Globe, ArrowRightLeft, Hotel, CheckCircle, Loader2, Radio, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

function StatusBadge({ connected, syncEnabled }) {
  if (!connected) return <Badge variant="outline" className="text-xs text-muted-foreground" data-testid="channex-status-disconnected">Not Enabled</Badge>;
  if (syncEnabled) return <Badge className="text-xs bg-emerald-600" data-testid="channex-status-synced">Active</Badge>;
  return <Badge variant="secondary" className="text-xs" data-testid="channex-status-connected">Connected</Badge>;
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-card/60 border border-border rounded-xl p-4 space-y-1">
      <div className="flex items-center gap-2 text-muted-foreground text-xs">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p className={`text-2xl font-bold mono ${accent || ''}`}>{value}</p>
    </div>
  );
}

export default function ChannelManagerTab({ hotelId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [polling, setPolling] = useState(false);
  const [otaBookings, setOtaBookings] = useState([]);
  const [step, setStep] = useState('loading');

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/channel-manager/${hotelId}/status`);
      const data = await res.json();
      setStatus(data);
      if (!data.connected) {
        setStep('disconnected');
      } else if (data.sync_enabled) {
        setStep('active');
      } else {
        setStep('disconnected');
      }
    } catch {
      setStep('disconnected');
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  const fetchOtaBookings = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/channel-manager/${hotelId}/ota-bookings`);
      const data = await res.json();
      setOtaBookings(data.bookings || []);
    } catch { /* ignore */ }
  }, [hotelId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { if (step === 'active') fetchOtaBookings(); }, [step, fetchOtaBookings]);

  const handleEnable = async () => {
    setEnabling(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/channel-manager/${hotelId}/enable`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || 'Channel Manager enabled!');
        fetchStatus();
      } else {
        toast.error(data.error || data.detail || 'Failed to enable');
      }
    } catch {
      toast.error('Failed to enable Channel Manager');
    } finally { setEnabling(false); }
  };

  const handleDisconnect = async () => {
    await fetch(`${BACKEND_URL}/api/channel-manager/${hotelId}/disconnect`, { method: 'POST' });
    toast.success('Channel Manager disabled');
    setStatus(null);
    setStep('disconnected');
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/channel-manager/${hotelId}/sync`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success('Availability & rates synced to OTAs');
        fetchStatus();
      } else {
        toast.error(data.error || 'Sync failed');
      }
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  const handlePollBookings = async () => {
    setPolling(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/channel-manager/${hotelId}/poll-bookings`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        if (data.ingested > 0) {
          toast.success(`${data.ingested} new OTA booking(s) received!`);
        } else {
          toast.info('No new bookings from OTAs');
        }
        fetchOtaBookings();
        fetchStatus();
      }
    } catch { toast.error('Poll failed'); }
    finally { setPolling(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="channel-manager-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold heading-font flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            Channel Manager
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Distribute your rooms to Booking.com, Airbnb, Expedia & 200+ OTAs
          </p>
        </div>
        <StatusBadge connected={status?.connected} syncEnabled={status?.sync_enabled} />
      </div>

      {/* Step: Not Enabled */}
      {step === 'disconnected' && (
        <Card data-testid="channex-enable-card">
          <CardContent className="p-6 space-y-5">
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold heading-font mb-2">Go Live on 200+ OTAs</h3>
              <p className="text-sm text-muted-foreground max-w-lg mx-auto">
                Enable Channel Manager to automatically distribute your rooms across Booking.com, Airbnb, Expedia, Agoda, Hotels.com and 200+ travel sites. We handle the setup — your rooms, rates, and availability sync instantly.
              </p>
            </div>

            {/* What you get */}
            <div className="grid sm:grid-cols-3 gap-3 max-w-xl mx-auto">
              <div className="bg-muted/30 rounded-xl p-3 text-center">
                <ArrowRightLeft className="w-5 h-5 mx-auto mb-1.5 text-primary" />
                <p className="text-xs font-medium">Auto-Sync</p>
                <p className="text-[10px] text-muted-foreground">Rooms & rates push to all OTAs automatically</p>
              </div>
              <div className="bg-muted/30 rounded-xl p-3 text-center">
                <Hotel className="w-5 h-5 mx-auto mb-1.5 text-primary" />
                <p className="text-xs font-medium">OTA Bookings</p>
                <p className="text-[10px] text-muted-foreground">Receive Booking.com, Airbnb bookings here</p>
              </div>
              <div className="bg-muted/30 rounded-xl p-3 text-center">
                <Shield className="w-5 h-5 mx-auto mb-1.5 text-primary" />
                <p className="text-xs font-medium">No Extra Cost</p>
                <p className="text-[10px] text-muted-foreground">Included with your getbitsy.ai plan</p>
              </div>
            </div>

            <div className="max-w-sm mx-auto">
              <Button
                onClick={handleEnable}
                disabled={enabling}
                className="w-full h-12 text-base"
                data-testid="channex-enable-btn"
              >
                {enabling ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Setting up your channels...</>
                ) : (
                  <><Zap className="w-5 h-5 mr-2" /> Enable Channel Manager</>
                )}
              </Button>
              <p className="text-[10px] text-muted-foreground text-center mt-2">
                Powered by Channex — connects to 200+ OTAs worldwide
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: Active */}
      {step === 'active' && (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="OTA Bookings" value={status?.ota_bookings || 0} icon={Globe} />
            <StatCard label="OTA Revenue" value={`$${(status?.ota_revenue || 0).toLocaleString()}`} icon={ArrowRightLeft} accent="text-emerald-500" />
            <StatCard label="OTA Commission" value={`$${(status?.ota_commission_total || 0).toLocaleString()}`} icon={ArrowRightLeft} accent="text-amber-500" />
            <StatCard label="Room Mappings" value={status?.room_mappings_count || 0} icon={Hotel} />
          </div>

          {/* Sync Controls */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="font-medium text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Channel Manager Active
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Last sync: {status?.last_sync ? new Date(status.last_sync).toLocaleString() : 'Never'} |
                    Last poll: {status?.last_poll ? new Date(status.last_poll).toLocaleString() : 'Never'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handlePollBookings} disabled={polling} data-testid="channex-poll-btn">
                    {polling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4 mr-1" />}
                    Pull Bookings
                  </Button>
                  <Button size="sm" onClick={handleSync} disabled={syncing} data-testid="channex-sync-btn">
                    {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                    Push ARI
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Background poller active (60s)
                </span>
                <span>Webhook: <code className="text-[10px] bg-muted px-1 rounded">/api/channel-manager/webhook</code></span>
              </div>
            </CardContent>
          </Card>

          {/* OTA Bookings Table */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm heading-font flex items-center gap-2">
                  <Globe className="w-4 h-4 text-primary" />
                  OTA Bookings ({otaBookings.length})
                </h3>
                <Button size="sm" variant="ghost" onClick={fetchOtaBookings} data-testid="channex-refresh-bookings-btn">
                  <RefreshCw className="w-3.5 h-3.5" />
                </Button>
              </div>

              {otaBookings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  <Globe className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No OTA bookings yet.</p>
                  <p className="text-xs mt-1">Bookings from Booking.com, Airbnb, Expedia etc. will appear here automatically.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ota-bookings-table">
                    <thead>
                      <tr className="border-b border-border text-left text-xs text-muted-foreground">
                        <th className="pb-2 pr-3">OTA</th>
                        <th className="pb-2 pr-3">Guest</th>
                        <th className="pb-2 pr-3">Room</th>
                        <th className="pb-2 pr-3">Check-in</th>
                        <th className="pb-2 pr-3">Check-out</th>
                        <th className="pb-2 pr-3">Amount</th>
                        <th className="pb-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {otaBookings.map((b) => (
                        <tr key={b.id} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="py-2 pr-3">
                            <Badge variant="secondary" className="text-[10px]">{b.ota_name}</Badge>
                          </td>
                          <td className="py-2 pr-3 text-xs">{b.guest_name}</td>
                          <td className="py-2 pr-3 text-xs">{b.room_type || 'N/A'}</td>
                          <td className="py-2 pr-3 text-xs mono">{b.check_in}</td>
                          <td className="py-2 pr-3 text-xs mono">{b.check_out}</td>
                          <td className="py-2 pr-3 text-xs mono font-semibold">${b.final_price}</td>
                          <td className="py-2">
                            {b.booking_status === 'confirmed' ? (
                              <Badge className="text-[10px] bg-emerald-600">Confirmed</Badge>
                            ) : b.booking_status === 'cancelled' ? (
                              <Badge variant="destructive" className="text-[10px]">Cancelled</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px]">{b.booking_status}</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Disconnect */}
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={handleDisconnect} className="text-xs text-muted-foreground">
              <Unlink className="w-3 h-3 mr-1" /> Disable Channel Manager
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
